/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi, SearchApi } from '@alfresco/js-api';
import { forkJoin, from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DmsMetadataService } from '../upload/dms-metadata.service';

/**
 * Authorised scope for "All documents".
 *
 * Deliberately typed-based rather than path-based. A `PATH:` clause forces the
 * query onto the Elasticsearch index, whose **path** data in this deployment
 * lags metadata by a long way — a document uploaded a minute ago matches
 * `ASPECT:"dms:documentInfo"` but not `... AND PATH:"/app:company_home//*"`,
 * so a path-scoped inventory silently hides new documents. A type-based scope
 * is answered from the repository's own metadata query and is always current.
 *
 * The search runs server-side and returns only nodes the caller may read, so
 * this is a presentation scope, not a security boundary.
 */
const SCOPE = '(TYPE:"cm:content" OR TYPE:"cm:folder") AND NOT TYPE:"cm:dictionaryModel"';

/**
 * Ceiling for the summary counts.
 *
 * The repository does not return a true hit count for these queries — its
 * `totalItems` is the size of the page plus one when more rows exist. So a count
 * is obtained by asking for up to this many ids and looking at whether the page
 * filled up; beyond the ceiling the number is reported as "at least".
 */
const COUNT_LIMIT = 1000;

/** Page size and ceiling when department values are read from the documents themselves. */
const DEPARTMENT_PAGE = 1000;
const DEPARTMENT_LIMIT = 5000;

/** Selected filter values; an empty string means "no filter". */
export interface DocumentFilters {
  documentType: string;
  department: string;
  status: string;
  creator: string;
  term: string;
}

export interface DocumentRow {
  id: string;
  name: string;
  isFolder: boolean;
  documentType: string;
  department: string;
  modifiedAt: string;
  createdAt: string;
  ownerId: string;
  ownerName: string;
  status: string;
  versionLabel: string;
  sizeInBytes?: number;
  mimeType?: string;
  title: string;
  description: string;
  confidentiality: string;
  documentCode: string;
}

export interface DocumentPage {
  rows: DocumentRow[];
  hasMoreItems: boolean;
  /** Exact number of matches, or `null` when the repository would not give one. */
  total: number | null;
}

/** A count that may be a lower bound rather than the exact figure. */
export interface CountValue {
  value: number;
  exact: boolean;
}

/** One selectable value of a facet, with the number of matching documents. */
export interface FacetOption {
  value: string;
  label: string;
  count: number;
}

export interface DocumentCounts {
  total: CountValue;
  draft: CountValue;
  pending: CountValue;
  approved: CountValue;
  expired: CountValue;
}

export type SortField = 'cm:modified' | 'cm:created' | 'cm:name';

/**
 * Server-side inventory of the documents in the repository (UI-04).
 *
 * Every list, count and filter value comes from one authorised Search API query;
 * nothing is aggregated by loading the repository into the browser.
 *
 * Three measured properties of this environment shape every query here:
 *
 * 1. Custom (`dms:`) properties match only in AFTS **exact-match** form —
 *    `=dms:status:"approved"` matches, `dms:status:"approved"` returns nothing.
 * 2. `totalItems` is not a true hit count for these queries: it comes back as
 *    the page size plus one whenever more rows exist. Exact totals are therefore
 *    only trusted when a page is not full — see {@link COUNT_LIMIT}.
 * 3. `PATH:` clauses are served from an index whose path data lags well behind
 *    metadata, so they are avoided entirely — see {@link SCOPE}.
 */
@Injectable({ providedIn: 'root' })
export class AllDocumentsService {
  private readonly api = inject(AlfrescoApiService);

  private get searchApi(): SearchApi {
    return new SearchApi(this.api.getInstance() as any);
  }

  private search(body: any): Observable<any> {
    return from(this.searchApi.search(body) as Promise<any>);
  }

  /** Moves a document or folder to the trash, where it can be restored. */
  trash(nodeId: string): Observable<unknown> {
    return from(new NodesApi(this.api.getInstance() as any).deleteNode(nodeId) as Promise<unknown>);
  }

  /** One page of the inventory, filtered and sorted server-side. */
  page(filters: DocumentFilters, sortField: SortField, ascending: boolean, skipCount: number, maxItems: number): Observable<DocumentPage> {
    return this.search({
      query: { query: this.buildQuery(filters), language: 'afts' },
      paging: { skipCount, maxItems },
      sort: [{ type: 'FIELD', field: sortField, ascending }],
      include: ['properties', 'path']
    }).pipe(
      map((res) => {
        const pagination = res?.list?.pagination ?? {};
        const hasMoreItems = !!pagination.hasMoreItems;
        return {
          rows: (res?.list?.entries ?? []).map((e: any) => this.toRow(e.entry)),
          hasMoreItems,
          // only a page that did not fill up tells us the real total
          total: hasMoreItems ? null : skipCount + (pagination.count ?? 0)
        };
      })
    );
  }

  /**
   * The five summary counts.
   *
   * Each one is a separate `maxItems: 1` query that reads `totalItems`, so the
   * numbers are the server's, not a count of whatever happens to be on screen.
   * The status counts respect the active filters, so the cards stay consistent
   * with the table below them.
   */
  counts(filters: DocumentFilters): Observable<DocumentCounts> {
    const base = this.buildQuery({ ...filters, status: '' });
    const withStatus = (status: string) => base + ' AND =dms:status:"' + status + '"';

    return forkJoin({
      total: this.count(base),
      draft: this.count(withStatus('draft')),
      pending: this.count(withStatus('pending')),
      approved: this.count(withStatus('approved')),
      // "expired" is the effectivity date having passed, the same rule the
      // document-expiry dashboard applies — not a status somebody typed
      expired: this.count(base + ' AND cm:to:[MIN TO NOW]')
    });
  }

  /**
   * Distinct values for the department and creator filters.
   *
   * These come from search facets, so a value only appears when a document the
   * user can see actually carries it — the filters can never offer a choice that
   * returns nothing.
   *
   * A facet on `dms:department` answers nothing in this deployment: the field is
   * not in the full-text index that facets read. When it comes back empty the
   * values are counted from the documents instead — see {@link departments}.
   */
  facets(filters: DocumentFilters): Observable<{ departments: FacetOption[]; creators: FacetOption[] }> {
    const query = this.buildQuery({ ...filters, department: '', creator: '' });
    return this.search({
      query: { query, language: 'afts' },
      paging: { maxItems: 1 },
      facetFields: {
        facets: [
          { field: 'dms:department', label: 'department', limit: 50 },
          { field: 'creator', label: 'creator', limit: 50 }
        ]
      }
    }).pipe(
      map((res) => {
        const groups: any[] = res?.list?.context?.facetsFields ?? [];
        const read = (label: string): FacetOption[] =>
          (groups.find((g) => g.label === label)?.buckets ?? []).map((b: any) => ({
            value: b.label,
            label: b.display || b.label,
            count: b.count ?? 0
          }));
        return { departments: read('department'), creators: read('creator') };
      }),
      switchMap((result) =>
        result.departments.length ? of(result) : this.departments(query).pipe(map((departments) => ({ ...result, departments })))
      )
    );
  }

  /**
   * Department values counted from the documents that carry DMS details,
   * newest first, a page of properties at a time up to {@link DEPARTMENT_LIMIT}.
   * The value is kept as stored, since the filter matches it exactly.
   */
  private departments(query: string): Observable<FacetOption[]> {
    const counts = new Map<string, number>();

    const read = (skipCount: number): Observable<FacetOption[]> =>
      this.search({
        query: { query: query + ' AND ASPECT:"dms:documentInfo"', language: 'afts' },
        paging: { skipCount, maxItems: DEPARTMENT_PAGE },
        include: ['properties'],
        fields: ['id', 'properties'],
        sort: [{ type: 'FIELD', field: 'cm:created', ascending: false }]
      }).pipe(
        switchMap((res) => {
          const entries: any[] = res?.list?.entries ?? [];
          for (const { entry } of entries) {
            const value = entry?.properties?.['dms:department'];
            if (typeof value === 'string' && value.trim()) {
              counts.set(value, (counts.get(value) ?? 0) + 1);
            }
          }
          const seen = skipCount + entries.length;
          if (res?.list?.pagination?.hasMoreItems && entries.length && seen < DEPARTMENT_LIMIT) {
            return read(seen);
          }
          return of(
            [...counts]
              .map(([value, count]) => ({ value, label: value.trim(), count }))
              .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'th'))
          );
        })
      );

    return read(0);
  }

  /** Thai label for a constrained value, shared with the upload screen. */
  label(value: string): string {
    return value ? DmsMetadataService.label(value) : '';
  }

  /**
   * Counts matches by asking for ids only, up to {@link COUNT_LIMIT}.
   *
   * `exact` is false when the page filled up, in which case the value is a lower
   * bound and the caller must present it as one.
   */
  private count(query: string): Observable<CountValue> {
    return this.search({
      query: { query, language: 'afts' },
      paging: { maxItems: COUNT_LIMIT, skipCount: 0 },
      fields: ['id']
    }).pipe(
      map((res) => {
        const pagination = res?.list?.pagination ?? {};
        return { value: pagination.count ?? 0, exact: !pagination.hasMoreItems };
      })
    );
  }

  private buildQuery(filters: DocumentFilters): string {
    const clauses = [SCOPE];

    // `dms:` properties only match in the exact-match form in this environment
    if (filters.documentType) {
      clauses.push('=dms:documentType:"' + this.escape(filters.documentType) + '"');
    }
    if (filters.department) {
      clauses.push('=dms:department:"' + this.escape(filters.department) + '"');
    }
    if (filters.status) {
      clauses.push('=dms:status:"' + this.escape(filters.status) + '"');
    }
    if (filters.creator) {
      clauses.push('creator:"' + this.escape(filters.creator) + '"');
    }
    if (filters.term) {
      // name/title only: a `TEXT:` clause would move the whole query onto the
      // lagging index, so full-text search belongs on the search page instead
      const term = this.escape(filters.term);
      clauses.push('(cm:name:"*' + term + '*" OR cm:title:"*' + term + '*")');
    }

    return clauses.join(' AND ');
  }

  /** Keeps user input from breaking out of the quoted AFTS term. */
  private escape(value: string): string {
    return value.replace(/["\\]/g, '\\$&');
  }

  private toRow(entry: any): DocumentRow {
    const props = entry.properties ?? {};

    return {
      id: entry.id,
      name: entry.name,
      isFolder: !!entry.isFolder,
      documentType: props['dms:documentType'] ?? '',
      department: props['dms:department'] ?? '',
      modifiedAt: entry.modifiedAt,
      createdAt: entry.createdAt,
      ownerId: entry.createdByUser?.id ?? '',
      ownerName: entry.createdByUser?.displayName ?? entry.createdByUser?.id ?? '',
      status: props['dms:status'] ?? '',
      versionLabel: props['cm:versionLabel'] ?? '',
      sizeInBytes: entry.content?.sizeInBytes,
      mimeType: entry.content?.mimeType,
      title: props['cm:title'] ?? '',
      description: props['cm:description'] ?? '',
      confidentiality: props['dms:confidentiality'] ?? '',
      documentCode: props['dms:documentCode'] ?? ''
    };
  }
}
